import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PhoneAndroid as AppIcon,
  Language as WebIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fonction pour parser les dates dans différents formats
  const parseDateOfBirth = (dateValue) => {
    if (!dateValue) return null;
    
    // Si c'est un Timestamp Firestore (avec méthode toDate)
    if (dateValue.toDate) {
      return dateValue.toDate();
    }
    
    // Si c'est un objet avec des secondes (Timestamp Firestore sérialisé)
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // Si c'est une chaîne de caractères
    if (typeof dateValue === 'string') {
      // Essayer de parser comme date ISO (ex: "2025-09-03")
      const isoDate = new Date(dateValue);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
      
      // Essayer de parser le format français (ex: "26 décembre 1964 à 01:00:00 UTC+1")
      const frenchDateMatch = dateValue.match(/(\d{1,2})\s+([a-zA-Zéû]+)\s+(\d{4})/);
      if (frenchDateMatch) {
        const day = parseInt(frenchDateMatch[1]);
        const monthName = frenchDateMatch[2].toLowerCase();
        const year = parseInt(frenchDateMatch[3]);
        
        const monthMap = {
          'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3, 'mai': 4, 'juin': 5,
          'juillet': 6, 'août': 7, 'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
        };
        
        const month = monthMap[monthName];
        if (month !== undefined) {
          return new Date(year, month, day);
        }
      }
      
      // Essayer de parser d'autres formats de date
      const parsedDate = new Date(dateValue);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    
    return null;
  };

  // Fonction pour formater les dates de naissance
  const formatDateOfBirth = (dateValue) => {
    const date = parseDateOfBirth(dateValue);
    if (!date) return '';
    
    return date.toLocaleDateString('fr-FR');
  };

  // Fonction pour récupérer la date brute pour la synchronisation
  const getRawDateOfBirth = (dateValue) => {
    const date = parseDateOfBirth(dateValue);
    if (!date) return null;
    
    // Retourner un objet similaire à un Timestamp Firestore
    return {
      seconds: Math.floor(date.getTime() / 1000),
      nanoseconds: 0
    };
  };

  // Fonction générique pour synchroniser avec une collection spécifique
  const syncWithCollection = async (users, collectionName, defaultFields) => {
    try {
      const collectionRef = collection(db, collectionName);
      const existingDocs = await getDocs(collectionRef);
      const existingIds = existingDocs.docs.map(doc => doc.id);

      // Ajouter/mettre à jour les documents
      await Promise.all(
        users.map(async (user) => {
          const docRef = doc(db, collectionName, user.id);
          
          // Préparer les données en conservant la date de naissance brute
          const data = {
            ...defaultFields,
            ...user,
            dateOfBirth: user.rawDateOfBirth, // Utiliser la date brute
            lastSynced: serverTimestamp()
          };

          await setDoc(docRef, data, { merge: true });
        })
      );

      // Supprimer les documents qui ne sont plus dans users
      const usersIds = users.map(user => user.id);
      const toDelete = existingIds.filter(id => !usersIds.includes(id));
      
      await Promise.all(
        toDelete.map(async (id) => {
          await deleteDoc(doc(db, collectionName, id));
        })
      );
    } catch (error) {
      console.error(`Erreur lors de la synchronisation avec ${collectionName}:`, error);
    }
  };

  // Synchroniser tous les rôles avec leurs collections respectives
  const syncAllRoleCollections = async (users) => {
    try {
      // Synchroniser les professeurs
      const teachers = users.filter(user => user.role === 'Professeur');
      await syncWithCollection(teachers, 'teachers', {
        firstName: '',
        lastName: '',
        email: '',
        pseudo: '',
        dateOfBirth: '',
        phone: '',
        specialties: '',
        availability: ''
      });

      // Synchroniser les élèves (adultes et enfants)
      const students = users.filter(user => 
        user.role === 'Élève' || user.role === 'Élève adulte'
      );
      await syncWithCollection(students, 'students', {
        firstName: '',
        lastName: '',
        email: '',
        pseudo: '',
        dateOfBirth: '',
        phone: '',
        level: '',
        emergencyContact: '',
        medicalInfo: '',
        isAdult: user => user.role === 'Élève adulte'
      });

      // Synchroniser les parents
      const parents = users.filter(user => user.role === 'Parent');
      await syncWithCollection(parents, 'parents', {
        firstName: '',
        lastName: '',
        email: '',
        pseudo: '',
        dateOfBirth: '',
        phone: '',
        children: [],
        address: ''
      });

    } catch (error) {
      console.error("Erreur lors de la synchronisation des rôles:", error);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Charger les inscriptions site web pour détecter la source 'site'
        const regsSnap = await getDocs(collection(db, 'registrations'));
        const siteEmails = new Set();
        regsSnap.docs.forEach(d => {
          const data = d.data();
          // Emails trouvés dans les inscriptions site web
          const email = data.contactInfo?.email || data.parentInfo?.email || data.email;
          if (email && data.source !== 'mobile_app') {
            siteEmails.add(email.toLowerCase());
          }
        });

        const querySnapshot = await getDocs(collection(db, 'users'));
        const usersList = [];
        
        for (const docSnap of querySnapshot.docs) {
          const data = docSnap.data();
          
          // Chercher la date de naissance dans différents champs possibles
          const dateOfBirthValue = data.dateOfBirth || data.birthday || data.dateOfExth || data.birthDate;
          
          // Formater la date de naissance correctement
          const formattedDateOfBirth = formatDateOfBirth(dateOfBirthValue);
          const rawDateOfBirth = getRawDateOfBirth(dateOfBirthValue);
          
          const isApp = data.registrationSource === 'mobile_app' ||
                        data.emailVerificationRequired === true ||
                        (data.pseudo && data.pseudo.trim() !== '');
          const isFromSite = !isApp && siteEmails.has((data.email || '').toLowerCase());
          const userSource = isApp ? 'app' : isFromSite ? 'site' : 'admin';

          usersList.push({
            id: docSnap.id,
            pseudo: data.pseudo || '',
            email: data.email || '',
            role: data.role || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            dateOfBirth: formattedDateOfBirth,
            rawDateOfBirth: rawDateOfBirth,
            source: userSource,
          });
        }

        setUsers(usersList);
        await syncAllRoleCollections(usersList);
        setLoading(false);
      } catch (error) {
        console.error('Erreur de chargement des utilisateurs:', error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      try {
        // Supprimer de la collection users
        await deleteDoc(doc(db, 'users', id));
        
        // Supprimer des collections de rôles si nécessaire
        const userToDelete = users.find(u => u.id === id);
        if (userToDelete) {
          if (userToDelete.role === 'Professeur') {
            await deleteDoc(doc(db, 'teachers', id));
          } else if (userToDelete.role === 'Élève' || userToDelete.role === 'Élève adulte') {
            await deleteDoc(doc(db, 'students', id));
          } else if (userToDelete.role === 'Parent') {
            await deleteDoc(doc(db, 'parents', id));
          }
        }

        setUsers(users.filter(user => user.id !== id));
      } catch (error) {
        console.error("Erreur de suppression:", error);
      }
    }
  };

  const columns = [
    { field: 'pseudo', headerName: 'Pseudo', width: 130 },
    { field: 'email', headerName: 'Email', width: 220 },
    {
      field: 'source',
      headerName: 'Source',
      width: 130,
      renderCell: (params) => {
        if (params.value === 'app')
          return <Chip icon={<AppIcon style={{ fontSize: 13 }} />} label="App mobile"
            size="small" color="success" variant="outlined"
            sx={{ '& .MuiChip-label': { fontSize: 11 } }} />;
        if (params.value === 'site')
          return <Chip icon={<WebIcon style={{ fontSize: 13 }} />} label="Site web"
            size="small" color="info" variant="outlined"
            sx={{ '& .MuiChip-label': { fontSize: 11 } }} />;
        return <Chip icon={<AdminIcon style={{ fontSize: 13 }} />} label="Admin"
          size="small" color="warning" variant="outlined"
          sx={{ '& .MuiChip-label': { fontSize: 11 } }} />;
      },
    },
    { 
      field: 'role', 
      headerName: 'Rôle', 
      width: 160,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          color={
            params.value === 'Professeur' ? 'primary' :
            params.value === 'Élève' ? 'secondary' :
            params.value === 'Élève adulte' ? 'info' :
            params.value === 'Parent' ? 'warning' : 'default'
          }
          variant="outlined"
          size="small"
        />
      )
    },
    { field: 'firstName', headerName: 'Prénom', width: 130 },
    { field: 'lastName', headerName: 'Nom', width: 130 },
    { 
      field: 'dateOfBirth', 
      headerName: 'Date de naissance', 
      width: 150,
      valueFormatter: (params) => params.value || 'Non renseignée'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <div>
          <Tooltip title="Modifier">
            <IconButton
              onClick={() => navigate(`/users/${params.id}`)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton
              onClick={() => handleDelete(params.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: '#7C3AED' }} />
            Gestion des utilisateurs
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/new')}
            sx={{
              backgroundColor: '#7C3AED',
              '&:hover': { backgroundColor: '#6D28D9' }
            }}
          >
            Ajouter utilisateur
          </Button>
        </Stack>
      </Paper>

      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          pageSize={10}
          loading={loading}
          components={{
            Toolbar: CustomToolbar,
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#EDE9FE',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#F5F3FF',
            }
          }}
        />
      </div>
    </Box>
  );
}

export default Users;